import { useState, useEffect } from "react";
import api, { errMsg } from "../lib/api";
import { plentiRows, money, money2, downloadPlentiPdf } from "../lib/plenti";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";

const cols = [
  ["Amount\nfinanced", false],
  ["Interest %", false],
  ["Tenure\n(Years)", false],
  ["Tenure\n(Months)", false],
  ["Amount to enter\non portal", true],
  ["Monthly fees", false],
  ["One-time fee", false],
  ["Total customer\npays", false],
  ["Monthly\nrepayment", true],
  ["Extra paid by\ncustomer", false],
];

export const PlentiCalculator = ({ config, editable = false, onConfigSaved }) => {
  const [amount, setAmount] = useState(6990);
  const [editOpen, setEditOpen] = useState(false);
  const rows = plentiRows(amount, config);

  return (
    <div className="space-y-5" data-testid="plenti-calculator">
      <div className="flex flex-wrap items-end gap-4">
        <label className="block">
          <span className="label-caps block mb-1">Amount to be financed</span>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
            <input type="number" min="0" step="1" value={amount} data-testid="plenti-amount-input"
              onChange={(e) => setAmount(e.target.value === "" ? "" : Number(e.target.value))}
              className="w-56 pl-7 pr-3 py-2.5 rounded-md border border-input bg-card text-base font-semibold focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none" />
          </div>
        </label>
        <div className="text-sm text-muted-foreground pb-1">
          Monthly account fee <b className="text-foreground">{money2(config.monthly_fee)}/mo</b> · One-time fee <b className="text-foreground">{money(config.one_time_fee)}</b>
        </div>
        {editable && (
          <button onClick={() => setEditOpen(true)} data-testid="plenti-edit-config"
            className="ml-auto inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-border text-sm font-semibold hover:bg-muted">
            <Icons.Settings2 size={15} /> Edit rates &amp; fees
          </button>
        )}
      </div>

      <div className="bg-card border border-border rounded-lg overflow-x-auto">
        <table className="w-full text-sm whitespace-nowrap">
          <thead>
            <tr className="border-b border-border bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
              {cols.map(([label, hl], i) => (
                <th key={i} className={`px-3 py-3 text-center align-bottom ${hl ? "bg-amber-100 text-amber-900" : ""}`}>
                  {label.split("\n").map((l, j) => <div key={j}>{l}</div>)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody data-testid="plenti-rows">
            {rows.map((r) => (
              <tr key={r.months} data-testid={`plenti-row-${r.months}`} className="border-b border-border/50 last:border-0 text-center">
                <td className="px-3 py-3 font-medium">{money(amount)}</td>
                <td className="px-3 py-3">{r.rate.toFixed(2)}%</td>
                <td className="px-3 py-3">{r.years}</td>
                <td className="px-3 py-3">{r.months}</td>
                <td className="px-3 py-3 bg-amber-50 font-bold text-primary">{money(r.portal)}</td>
                <td className="px-3 py-3">{money(r.monthlyFees)}</td>
                <td className="px-3 py-3">{money(r.oneTime)}</td>
                <td className="px-3 py-3">{money(r.total)}</td>
                <td className="px-3 py-3 bg-amber-50 font-bold text-primary">{money2(r.monthly)}</td>
                <td className="px-3 py-3 text-muted-foreground">{money(r.extra)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-muted-foreground max-w-3xl">
        <b>Portal amount</b> = amount financed ÷ (1 − merchant rate) — the value to enter on the Plenti portal so you net the financed amount.
        The <b>monthly repayment</b> already includes the {money2(config.monthly_fee)}/mo account fee and the {money(config.one_time_fee)} one-time fee spread across the term. Figures are indicative.
      </p>

      <div>
        <button onClick={() => downloadPlentiPdf(amount, config)} data-testid="plenti-download-pdf"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90 transition-opacity">
          <Icons.Download size={16} /> Download this calculation as PDF
        </button>
      </div>

      {editable && <EditConfigDialog open={editOpen} setOpen={setEditOpen} config={config} onSaved={onConfigSaved} />}
    </div>
  );
};

const EditConfigDialog = ({ open, setOpen, config, onSaved }) => {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        tenures: (config.tenures || []).map((t) => ({ ...t })),
        monthly_fee: config.monthly_fee, one_time_fee: config.one_time_fee,
      });
    }
  }, [open, config]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/plenti/config", {
        tenures: form.tenures.map((t) => ({ months: Number(t.months), rate: Number(t.rate) })),
        monthly_fee: Number(form.monthly_fee), one_time_fee: Number(form.one_time_fee),
      });
      toast.success("Plenti rates updated");
      setOpen(false); onSaved?.();
    } catch (e) { toast.error(errMsg(e)); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="w-[calc(100%-2rem)] max-w-lg" data-testid="plenti-config-dialog">
        <DialogHeader><DialogTitle>Edit Plenti rates &amp; fees</DialogTitle>
          <DialogDescription>Merchant interest rate per term, plus account and one-time fees.</DialogDescription></DialogHeader>
        {form && (
          <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
            <div className="grid grid-cols-2 gap-3">
              <label className="text-sm">Monthly account fee ($)
                <input type="number" step="0.01" value={form.monthly_fee} data-testid="plenti-monthly-fee"
                  onChange={(e) => setForm({ ...form, monthly_fee: e.target.value })}
                  className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background text-sm" /></label>
              <label className="text-sm">One-time fee ($)
                <input type="number" step="0.01" value={form.one_time_fee} data-testid="plenti-onetime-fee"
                  onChange={(e) => setForm({ ...form, one_time_fee: e.target.value })}
                  className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background text-sm" /></label>
            </div>
            <div className="space-y-2">
              <div className="label-caps">Interest rate per term (%)</div>
              {form.tenures.map((t, i) => (
                <div key={t.months} className="flex items-center gap-3">
                  <span className="w-28 text-sm text-muted-foreground">{t.months} months</span>
                  <input type="number" step="0.01" value={t.rate} data-testid={`plenti-rate-${t.months}`}
                    onChange={(e) => { const tn = [...form.tenures]; tn[i] = { ...tn[i], rate: e.target.value }; setForm({ ...form, tenures: tn }); }}
                    className="w-32 px-3 py-2 rounded-md border border-input bg-background text-sm" />
                  <span className="text-sm text-muted-foreground">%</span>
                </div>
              ))}
            </div>
          </div>
        )}
        <DialogFooter>
          <button onClick={() => setOpen(false)} className="px-4 py-2 rounded-md border border-border text-sm font-semibold">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="plenti-config-save"
            className="px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-semibold disabled:opacity-60">{saving ? "Saving…" : "Save"}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
